import * as THREE from 'three';
import { WizardType } from '../wizards/WizardTypes';

/**
 * Manages enhanced spell charge-up visuals, wizard-specific spell variations,
 * and victory/defeat particle explosions.
 *
 * All particle systems use pre-allocated BufferGeometry for GPU efficiency.
 */
export class SpellEffectsManager {
  private scene: THREE.Scene;

  // Pooled geometry for charge-up swirl particles
  private swirlGeometry: THREE.BufferGeometry;
  private swirlMaterial: THREE.PointsMaterial;
  private swirlPoints: THREE.Points | null = null;
  private swirlActive: boolean = false;
  private static readonly SWIRL_COUNT = 40;

  // Victory/defeat effect tracking
  private activeEffects: { points: THREE.Points; light: THREE.PointLight; elapsed: number; animId: number }[] = [];

  constructor(scene: THREE.Scene) {
    this.scene = scene;

    // Pre-allocate swirl particle system
    const positions = new Float32Array(SpellEffectsManager.SWIRL_COUNT * 3);
    const colors = new Float32Array(SpellEffectsManager.SWIRL_COUNT * 3);
    this.swirlGeometry = new THREE.BufferGeometry();
    this.swirlGeometry.setAttribute('position', new THREE.BufferAttribute(positions, 3));
    this.swirlGeometry.setAttribute('color', new THREE.BufferAttribute(colors, 3));

    this.swirlMaterial = new THREE.PointsMaterial({
      size: 0.08,
      vertexColors: true,
      transparent: true,
      opacity: 0.9,
      blending: THREE.AdditiveBlending,
    });
  }

  /**
   * Start swirling charge particles around a position.
   * Called when a spell begins charging.
   */
  startChargeSwirl(position: THREE.Vector3, color: THREE.Color): void {
    if (this.swirlPoints) {
      this.scene.remove(this.swirlPoints);
    }

    // Reset particle positions in a ring
    const positions = this.swirlGeometry.attributes.position.array as Float32Array;
    const colorArr = this.swirlGeometry.attributes.color.array as Float32Array;

    for (let i = 0; i < SpellEffectsManager.SWIRL_COUNT; i++) {
      const angle = (i / SpellEffectsManager.SWIRL_COUNT) * Math.PI * 2;
      const r = 0.4 + Math.random() * 0.2;
      positions[i * 3] = Math.cos(angle) * r;
      positions[i * 3 + 1] = Math.random() * 0.5;
      positions[i * 3 + 2] = Math.sin(angle) * r;

      colorArr[i * 3] = color.r * (0.8 + Math.random() * 0.2);
      colorArr[i * 3 + 1] = color.g * (0.8 + Math.random() * 0.2);
      colorArr[i * 3 + 2] = color.b * (0.8 + Math.random() * 0.2);
    }
    this.swirlGeometry.attributes.position.needsUpdate = true;
    this.swirlGeometry.attributes.color.needsUpdate = true;

    this.swirlPoints = new THREE.Points(this.swirlGeometry, this.swirlMaterial);
    this.swirlPoints.position.copy(position);
    this.scene.add(this.swirlPoints);
    this.swirlActive = true;
  }

  /**
   * Update charge-up swirl: particles spiral inward as charge progresses.
   */
  updateChargeSwirl(progress: number, position: THREE.Vector3): void {
    if (!this.swirlActive || !this.swirlPoints) return;

    this.swirlPoints.position.copy(position);
    const positions = this.swirlGeometry.attributes.position.array as Float32Array;
    const time = Date.now() * 0.003;
    const radius = 0.6 * (1 - progress * 0.6); // Spiral inward

    for (let i = 0; i < SpellEffectsManager.SWIRL_COUNT; i++) {
      const baseAngle = (i / SpellEffectsManager.SWIRL_COUNT) * Math.PI * 2;
      const angle = baseAngle + time * (2 + progress * 4); // Speed up with progress
      const r = radius + Math.sin(time * 3 + i) * 0.08;
      const yOffset = Math.sin(time * 2 + i * 0.5) * 0.15 * (1 - progress);

      positions[i * 3] = Math.cos(angle + i * 0.1) * r;
      positions[i * 3 + 1] = 0.1 + yOffset + progress * 0.3;
      positions[i * 3 + 2] = Math.sin(angle + i * 0.1) * r;
    }
    this.swirlGeometry.attributes.position.needsUpdate = true;
    this.swirlMaterial.opacity = 0.5 + progress * 0.5;
    this.swirlMaterial.size = 0.06 + progress * 0.06;
  }

  /** Stop and hide charge-up swirl */
  stopChargeSwirl(): void {
    if (this.swirlPoints) {
      this.scene.remove(this.swirlPoints);
      this.swirlPoints = null;
    }
    this.swirlActive = false;
  }

  /**
   * Get spell color variation based on wizard type.
   * Different wizards cast visually distinct spells.
   */
  getSpellColorForWizard(spellType: 'fireball' | 'iceShard' | 'shield', wizardType: WizardType): THREE.Color {
    if (spellType === 'fireball') {
      switch (wizardType) {
        case 'pyromancer': return new THREE.Color(0xFF3300); // Intense red-orange
        case 'frostmage': return new THREE.Color(0xFF8866); // Pale fire
        case 'archmage': return new THREE.Color(0xBB44FF); // Arcane purple fire
        case 'battlemage': return new THREE.Color(0xFFAA00); // Golden fire
        default: return new THREE.Color(0xFF6B35);
      }
    }
    if (spellType === 'iceShard') {
      switch (wizardType) {
        case 'pyromancer': return new THREE.Color(0xAADDFF); // Light frost
        case 'frostmage': return new THREE.Color(0x4488FF); // Deep blue ice
        case 'archmage': return new THREE.Color(0xAA88FF); // Arcane ice
        case 'battlemage': return new THREE.Color(0x88FFCC); // Green tint
        default: return new THREE.Color(0x87CEEB);
      }
    }
    // shield
    switch (wizardType) {
      case 'pyromancer': return new THREE.Color(0xFF8844); // Fiery shield
      case 'frostmage': return new THREE.Color(0x88CCFF); // Icy shield
      case 'archmage': return new THREE.Color(0xC9A227); // Golden arcane
      case 'battlemage': return new THREE.Color(0x44FF88); // Fortified green
      default: return new THREE.Color(0xC9A227);
    }
  }

  /**
   * Create a spectacular victory explosion at a wizard's position.
   */
  createVictoryEffect(position: THREE.Vector3): void {
    const count = 120;
    const positions = new Float32Array(count * 3);
    const colors = new Float32Array(count * 3);
    const velocities: THREE.Vector3[] = [];

    const gold = new THREE.Color(0xFFD700);
    const white = new THREE.Color(0xFFFFFF);

    for (let i = 0; i < count; i++) {
      positions[i * 3] = position.x;
      positions[i * 3 + 1] = position.y + 2;
      positions[i * 3 + 2] = position.z;

      const c = i % 3 === 0 ? gold : white;
      colors[i * 3] = c.r;
      colors[i * 3 + 1] = c.g;
      colors[i * 3 + 2] = c.b;

      const theta = Math.random() * Math.PI * 2;
      const phi = Math.random() * Math.PI;
      const speed = 3 + Math.random() * 5;
      velocities.push(new THREE.Vector3(
        Math.sin(phi) * Math.cos(theta) * speed,
        Math.cos(phi) * speed * 0.5 + 2, // upward bias
        Math.sin(phi) * Math.sin(theta) * speed,
      ));
    }

    const geometry = new THREE.BufferGeometry();
    geometry.setAttribute('position', new THREE.BufferAttribute(positions, 3));
    geometry.setAttribute('color', new THREE.BufferAttribute(colors, 3));

    const material = new THREE.PointsMaterial({
      size: 0.2,
      vertexColors: true,
      transparent: true,
      opacity: 1,
      blending: THREE.AdditiveBlending,
    });

    const points = new THREE.Points(geometry, material);
    this.scene.add(points);

    const light = new THREE.PointLight(0xFFD700, 3, 15);
    light.position.set(position.x, position.y + 2, position.z);
    this.scene.add(light);

    let elapsed = 0;
    const entry = { points, light, elapsed: 0, animId: 0 };

    const animate = () => {
      elapsed += 0.016;
      entry.elapsed = elapsed;

      const pos = geometry.attributes.position.array as Float32Array;
      for (let i = 0; i < count; i++) {
        pos[i * 3] += velocities[i].x * 0.016;
        pos[i * 3 + 1] += velocities[i].y * 0.016;
        pos[i * 3 + 2] += velocities[i].z * 0.016;
        velocities[i].y -= 3 * 0.016; // gravity
      }
      geometry.attributes.position.needsUpdate = true;

      material.opacity = Math.max(0, 1 - elapsed / 1.5);
      light.intensity = 3 * Math.max(0, 1 - elapsed / 1.2);

      if (elapsed < 1.5) {
        entry.animId = requestAnimationFrame(animate);
      } else {
        this.scene.remove(points);
        this.scene.remove(light);
        geometry.dispose();
        material.dispose();
        light.dispose();
        const idx = this.activeEffects.indexOf(entry);
        if (idx !== -1) this.activeEffects.splice(idx, 1);
      }
    };

    entry.animId = requestAnimationFrame(animate);
    this.activeEffects.push(entry);
  }

  /**
   * Create a defeat explosion (dark, falling sparks).
   */
  createDefeatEffect(position: THREE.Vector3): void {
    const count = 80;
    const positions = new Float32Array(count * 3);
    const colors = new Float32Array(count * 3);
    const velocities: THREE.Vector3[] = [];

    for (let i = 0; i < count; i++) {
      positions[i * 3] = position.x + (Math.random() - 0.5) * 1.5;
      positions[i * 3 + 1] = position.y + 1 + Math.random() * 2;
      positions[i * 3 + 2] = position.z + (Math.random() - 0.5) * 1.5;

      const shade = 0.2 + Math.random() * 0.3;
      colors[i * 3] = shade;
      colors[i * 3 + 1] = shade * 0.1;
      colors[i * 3 + 2] = shade * 0.1;

      velocities.push(new THREE.Vector3(
        (Math.random() - 0.5) * 4,
        (Math.random() - 0.5) * 2 - 1,
        (Math.random() - 0.5) * 4,
      ));
    }

    const geometry = new THREE.BufferGeometry();
    geometry.setAttribute('position', new THREE.BufferAttribute(positions, 3));
    geometry.setAttribute('color', new THREE.BufferAttribute(colors, 3));

    const material = new THREE.PointsMaterial({
      size: 0.15,
      vertexColors: true,
      transparent: true,
      opacity: 1,
      blending: THREE.AdditiveBlending,
    });

    const points = new THREE.Points(geometry, material);
    this.scene.add(points);

    const light = new THREE.PointLight(0xFF2200, 2, 10);
    light.position.set(position.x, position.y + 1, position.z);
    this.scene.add(light);

    let elapsed = 0;
    const entry = { points, light, elapsed: 0, animId: 0 };

    const animate = () => {
      elapsed += 0.016;
      entry.elapsed = elapsed;

      const pos = geometry.attributes.position.array as Float32Array;
      for (let i = 0; i < count; i++) {
        pos[i * 3] += velocities[i].x * 0.016;
        pos[i * 3 + 1] += velocities[i].y * 0.016;
        pos[i * 3 + 2] += velocities[i].z * 0.016;
        velocities[i].y -= 5 * 0.016; // heavier gravity
      }
      geometry.attributes.position.needsUpdate = true;

      material.opacity = Math.max(0, 1 - elapsed / 1.2);
      light.intensity = 2 * Math.max(0, 1 - elapsed / 0.8);

      if (elapsed < 1.2) {
        entry.animId = requestAnimationFrame(animate);
      } else {
        this.scene.remove(points);
        this.scene.remove(light);
        geometry.dispose();
        material.dispose();
        light.dispose();
        const idx = this.activeEffects.indexOf(entry);
        if (idx !== -1) this.activeEffects.splice(idx, 1);
      }
    };

    entry.animId = requestAnimationFrame(animate);
    this.activeEffects.push(entry);
  }

  dispose(): void {
    this.stopChargeSwirl();
    this.swirlGeometry.dispose();
    this.swirlMaterial.dispose();

    for (const effect of this.activeEffects) {
      cancelAnimationFrame(effect.animId);
      this.scene.remove(effect.points);
      this.scene.remove(effect.light);
      effect.points.geometry.dispose();
      (effect.points.material as THREE.Material).dispose();
      effect.light.dispose();
    }
    this.activeEffects = [];
  }
}
